type SupportedMediaElement = HTMLVideoElement | HTMLAudioElement;

// Type guard for checking if element is a supported media type
const isSupportedMediaElement = (element: any): element is SupportedMediaElement => {
	return element instanceof HTMLVideoElement || element instanceof HTMLAudioElement;
};

// We should never leave callers awaiting playback indefinitely. This value is a guardrail:
// if Safari (or a network edge case) never settles the play() promise, we fail fast.
const PLAY_TIMEOUT_MS = 5_000;

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
	return await new Promise<T>((resolve, reject) => {
		const timeoutId = window.setTimeout(() => {
			reject(new Error(`Timed out waiting for media play() after ${timeoutMs}ms`));
		}, timeoutMs);

		promise
			.then((value) => {
				window.clearTimeout(timeoutId);
				resolve(value);
			})
			.catch((error) => {
				window.clearTimeout(timeoutId);
				reject(error);
			});
	});
};

// Centralized map for debounce timers
const pauseDebounceTimers = new Map<string, NodeJS.Timeout>();

/**
 * Media (video + audio) control utility functions
 */
export const MediaControls = {
	/**
	 * Play media with autoplay fallback
	 */
	play: async (element: any): Promise<boolean> => {
		if (!isSupportedMediaElement(element)) {
			console.warn('Unsupported media element. Only HTMLVideoElement and HTMLAudioElement are supported.');
			return false;
		}

		try {
			// Important: don't wait for canplay/canplaythrough before calling play().
			// With preload="metadata" (and Safari in particular), canplay may never fire
			// until play() is invoked, which can deadlock callers awaiting playback
			// (e.g., global media orchestration waiting forever).
			//
			// Calling play() is the correct "kick off loading + playback" primitive; the
			// media element will buffer as needed.
			await withTimeout(element.play(), PLAY_TIMEOUT_MS);
			return true;
		} catch (error) {
			if (error instanceof Error && error.name === 'NotAllowedError') {
				// Try with muted if autoplay was blocked
				element.muted = true;
				try {
					await withTimeout(element.play(), PLAY_TIMEOUT_MS);
					return true;
				} catch (innerError) {
					// If playback never starts (or fails), ensure we don't leave a pending play request.
					try {
						element.pause();
					} catch {
						// no-op
					}
					console.error('Failed to play media even when muted:', innerError);
					return false;
				}
			} else {
				// If playback never starts (or fails), ensure we don't leave a pending play request.
				try {
					element.pause();
				} catch {
					// no-op
				}
				console.error('Error playing media:', error);
				return false;
			}
		}
	},

	/**
	 * Debounced pause with timeout clearing - always use this instead of direct pause
	 */
	pause: (element: any, callback?: () => void, delay = 100) => {
		if (!isSupportedMediaElement(element)) {
			console.warn('Unsupported media element. Only HTMLVideoElement and HTMLAudioElement are supported.');
			return;
		}

		if (pauseDebounceTimers.has(element.id)) {
			clearTimeout(pauseDebounceTimers.get(element.id));
		}

		const timerId = setTimeout(() => {
			if (!element.paused) {
				element.pause();
				callback?.();
			}
			pauseDebounceTimers.delete(element.id);
		}, delay);

		pauseDebounceTimers.set(element.id, timerId);
	},

	/**
	 * Checks if an element is a supported media type
	 */
	validateMediaElement: (element: any): boolean => {
		const isValid = isSupportedMediaElement(element);
		if (!isValid) {
			console.warn('Unsupported media element. Only HTMLVideoElement and HTMLAudioElement are supported.');
		}
		return isValid;
	},
};
